import React, { Component } from "react";
import { AppBadge, AppCard, DataTableToolbar } from "../../components/ui";
import cx from "classnames";
import { User } from "../../models/User";
import { Column, ColumnFilterElementTemplateOptions } from "primereact/column";
import { CheckCircle, XCircle } from "lucide-react";
import history from "../../history";
import { dateFilterTemplate, numberFilterMatchModeOptions, statusFilterMatchModeOptions, stringFilterMatchModeOptions } from "../Paginator";
import { dateBodyTemplate } from "../../utils/columnTemplates";
import { userApiService } from "../../services/api/users/UsersApiService";
import { IFilterModel, SearchRequestBaseModel, SortOrderType } from "../../models/SearchRequestModel";
import { toastService } from "../../services/toastService";
import ExtendedDataTable from "../../components/ExtendedDataTable";
import { Link } from "react-router-dom";
import { TriStateCheckbox } from "primereact/tristatecheckbox";
import { SelectItem } from "../../models/SelectItem";
import { merchantApiService } from "../../services/api/merchants/MerchantApiService";
import { Dropdown } from "primereact/dropdown";
import { utils } from "../../utils/utils";
import { isSuperAdmin } from "../../authGuard";

interface IndividualsProps {
    merchantId: number | null;
    onClear: () => void;
    isDashboard: boolean;
}

interface IndividualsState {
    individuals: User[];
    merchants: SelectItem[];
    isLoading: boolean;
    isDashboard: boolean;
    totalRecords: number;
    selectedStatusFilter: string | null;
    fromSearchFilters: IFilterModel[]
}

export default class Individuals extends Component<IndividualsProps, IndividualsState> {
    unlisten: (() => void) | null = null;

    userStatusClassesMap: Map<string, string> = new Map<string, string>([
        ["Active", "success"],
        ["Inactive", "warning"],
        ["Deactivated", "danger"],
        ["New", "success"],
        ["Pending Verification", "warning"],
        ["Verified", "success"],
        ["Blocked", "danger"],
    ]);
    statusTypes: Map<number, string> = new Map<number, string>();
    private dataTable: ExtendedDataTable | null = null;

    constructor(props: IndividualsProps | Readonly<IndividualsProps>) {
        super(props);

        this.state = {
            individuals: [],
            merchants: [],
            isLoading: true,
            isDashboard: false,
            totalRecords: 0,
            selectedStatusFilter: null,
            fromSearchFilters: []
        };
    }

    statusFilterTemplate = (options: ColumnFilterElementTemplateOptions) => {
        const { selectedStatusFilter } = this.state;

        return (
            <Dropdown
                value={selectedStatusFilter}
                options={[
                    { label: "Active", value: "200" },
                    { label: "Closed", value: "350" },
                    { label: "Deactivated", value: "300" },
                    { label: "New", value: "100" },
                    { label: "Pending Verification", value: "150" },
                    { label: "Blocked", value: "400" },
                ]}
                onChange={(e) => {
                    this.setState({ selectedStatusFilter: e.value });
                    options.filterCallback(e.value);
                }}
                placeholder="Select Status"
            />
        );
    };

    async componentDidMount() {
        this.loadMerchantsAndStatusData();
        await this.handleSearchParams();

        this.unlisten = history.listen(async (location) => {
            console.log("listener called on in the individual=============")
            if ((location.action == "PUSH" || location.action == "REPLACE") && location.location.pathname == "/users") {
                await this.handleSearchParams();
            }
        });
    }

    componentWillUnmount() {
        // Clean up the history listener
        if (this.unlisten) {
            this.unlisten();
        }

        // Reset all state
        this.setState({
            individuals: [],
            totalRecords: 0,
            fromSearchFilters: [],
            selectedStatusFilter: null
        });

        // Reset the datatable
        this.dataTable?.reset();
    }

    loadMerchantsAndStatusData = async () => {
        const statusTypes = await userApiService.getStatusTypes();

        if (!statusTypes.success) {
            this.setState({ isLoading: false });
            toastService.error(statusTypes.message);
            return;
        }

        statusTypes.data.forEach((item) => {
            this.statusTypes.set(item.value, item.label);
        });
        const merchantResult = await merchantApiService.getSelectItem();

        this.setState({ merchants: merchantResult.success ? merchantResult.data : [] });
    };

    handleSearchParams = async () => {
        const queryParams = new URLSearchParams(history.location.search);
        const totalUsers = queryParams.get("totalUsers");
        const userData = queryParams.get("userData");
        const searchInput = queryParams.get("searchInput");

        if (searchInput) {
            console.log("State Reset first when search from Global search ======")
            // Reset state first
            this.setState({
                individuals: [],
                totalRecords: 0,
                fromSearchFilters: [],
            });
        }

        if (totalUsers && userData && searchInput) {
            const users = await JSON.parse(userData) as User[];
            const filters = await JSON.parse(searchInput) as IFilterModel[];

            // Set new state with fresh data
            this.setState({
                fromSearchFilters: filters,
                totalRecords: Number(totalUsers),
                individuals: users.filter(f => f.id != 0),
                isLoading: false,
            });
            this.walletBalance(users ?? []);
        }
    };

    loadUsersData = async (searchRequest: SearchRequestBaseModel) => {
        // If we have search filters from URL or new filters from datatable
        if (history.location.search == "" || searchRequest.filters.length != 0 || this.state.fromSearchFilters.length > 0) {
            this.setState({ isLoading: true });

            // Use fromSearchFilters if they exist, otherwise use the provided filters
            searchRequest.filters = utils.manageDataTableFilters(
                (this.state.fromSearchFilters.length > 0 && searchRequest.filters.length == 0) ? this.state.fromSearchFilters : searchRequest.filters
            );

            const usersResult = await userApiService.onlyIndividuals(searchRequest);

            if (!usersResult.success) {
                toastService.error(usersResult.message);
                this.setState({ isLoading: false });
                return;
            }

            this.setState({
                individuals: usersResult.data,
                totalRecords: usersResult.totalCount,
                isLoading: false,
                fromSearchFilters: []
            });
            await this.walletBalance(usersResult.data ?? []);
        }
    };

    walletBalance = async (users: User[]) => {
        users
            .filter((a) => a.cbsOnboarded)
            .forEach((user) => {
                userApiService
                    .getUserBalance(user.id)
                    .then((item) => {
                        const stateUsers = this.state.individuals;
                        const index = stateUsers.findIndex((a) => a.id == user.id);
                        stateUsers[index].walletBalance = item.data?.walletBalance;
                        stateUsers[index].walletId = item.data?.walletId;
                        stateUsers[index].currency = item.data?.currency;

                        this.setState({ individuals: stateUsers });
                    })
                    .catch((_) => { });
            });
    };

    renderHeader = (hasActiveFilters: boolean) => <DataTableToolbar onClear={() => { this.props.onClear(); history.replace({ pathname: "/users" }); this.dataTable!.reset(); }} hasActiveFilters={hasActiveFilters} />;

    onNameClick = (userId: number) => {
        history.push(`/user-profile/${userId}`);
    };

    onRowClick = (event: any) => {
        const user = event.data as User;
        history.push(`/user-profile/${user.id}`);
    };

    renderStatusBadge(success: number, title: string) {
        const item = this.statusTypes.get(success);
        const className = this.userStatusClassesMap.get(item ?? "") ?? "danger";
        return <AppBadge variant={className as any}>{title}</AppBadge>;
    }

    verifiedBodyTemplate = (rowData: User) => {
        return rowData.cbsOnboarded ? (
            <CheckCircle size={20} className="text-success" />
        ) : (
            <XCircle size={20} className="text-danger" />
        );
    };

    verifiedFilterTemplate = (options: ColumnFilterElementTemplateOptions) => {
        return <TriStateCheckbox value={options.value} onChange={(e) => options.filterCallback(e.value)} />;
    };
    SearchIndividuals = () => {
        console.log("searching...");
        var searchRequest = new SearchRequestBaseModel();

        this.loadUsersData(searchRequest);
    };
    merchantFilterTemplate = (options: ColumnFilterElementTemplateOptions) => {
        return (
            <Dropdown
                value={options.value}
                options={this.state.merchants}
                onChange={(e) => options.filterCallback(e.value)}
                optionLabel="label"
                optionValue="value"
                filter
                showClear
                placeholder="Select Merchants"
                className="p-column-filter"
            />
        );
    };

    render() {
        const table = (
                                        <ExtendedDataTable
                                            header={this.renderHeader}
                                            value={this.state.individuals}
                                            loading={this.state.isLoading}
                                            totalRecords={this.state.totalRecords}
                                            onChange={this.loadUsersData}
                                            ref={(ref) => (this.dataTable = ref)}
                                            defaultSortOrder={SortOrderType.Descending}
                                            defaultSortField="createdTimeStamp"
                                            
                                            onRowClick={this.onRowClick}
                                        >
                                            <Column {...({ alwaysVisible: true } as any)} field="id" header="Id" filterMatchModeOptions={numberFilterMatchModeOptions}></Column>
                                            <Column
                                                field="firstName"
                                                header="First Name"
                                                {...({ alwaysVisible: true } as any)}
                                                body={(rowData: User) => <Link to={`/user-profile/${rowData.id}`}>{rowData.firstName}</Link>}
                                                filterMatchModeOptions={stringFilterMatchModeOptions}
                                            ></Column>
                                               <Column
                                                field="middleName"
                                                header="Middle Name"
                                                body={(rowData: User) => <Link to={`/user-profile/${rowData.id}`}>{rowData.middleName}</Link>}
                                                filterMatchModeOptions={stringFilterMatchModeOptions}
                                            ></Column>
                                            
                                            <Column
                                                field="lastName"
                                                header="Last Name"
                                                {...({ alwaysVisible: true } as any)}
                                                body={(rowData: User) => <Link to={`/user-profile/${rowData.id}`}>{rowData.lastName}</Link>}
                                                filterMatchModeOptions={stringFilterMatchModeOptions}
                                            ></Column>
                                            <Column  {...({ alwaysVisible: true } as any)} field="email" header="Email" filterMatchModeOptions={stringFilterMatchModeOptions}></Column>
                                            <Column
                                                field="walletId"
                                                header="Wallet Id"
                                                body={(row: User) => (row.cbsOnboarded ? row.walletId : null)}
                                                headerStyle={{ minWidth: "120px" }}
                                            ></Column>
                                            <Column
                                                field="walletBalance"
                                                header="Balance"
                                                body={(row: User) => (row.cbsOnboarded ? row.walletBalance : null)}
                                                headerStyle={{ minWidth: "120px" }}
                                            ></Column>
                                            <Column field="currency" header="Currency" headerStyle={{ minWidth: "120px" }}></Column>
                                            <Column
                                                field="merchant"
                                                filterMatchMode="equals"
                                                header="Merchant"
                                                filterField="MerchantId"
                                                showFilterMatchModes={false}
                                                filterElement={this.merchantFilterTemplate}
                                                filterMatchModeOptions={isSuperAdmin() ? stringFilterMatchModeOptions : null}
                                                headerStyle={{ minWidth: "120px" }}
                                            ></Column>
                                            <Column
                                                field="merchantGroup"
                                                header="Merchant Group"
                                                filterMatchModeOptions={isSuperAdmin() ? stringFilterMatchModeOptions : null}
                                                headerStyle={{ minWidth: "120px" }}
                                            ></Column>
                                            <Column
                                                field="createdTimeStamp"
                                                header="Created TimeStamp"
                                                body={dateBodyTemplate("createdTimeStamp")}
                                                filterMatchModeOptions={numberFilterMatchModeOptions}
                                                dataType="date"
                                                filterElement={dateFilterTemplate}
                                            ></Column>
                                            <Column
                                                field="lastLogin"
                                                header="Last Login"
                                                body={dateBodyTemplate("lastLogin")}
                                                filterMatchModeOptions={numberFilterMatchModeOptions}
                                                dataType="date"
                                                filterElement={dateFilterTemplate}
                                            ></Column>
                                            <Column
                                                field="status"
                                                header="Status"
                                                filterMatchMode="equals"
                                                filterMatchModeOptions={statusFilterMatchModeOptions}
                                                filterElement={this.statusFilterTemplate}
                                                body={(rowData: User) =>
                                                    this.renderStatusBadge(rowData.status, this.statusTypes.get(rowData.status)!)
                                                }
                                            ></Column>
                                            <Column
                                                field="cbsOnboarded"
                                                header="CBS Onboarded"
                                                dataType="boolean"
                                                filterMatchMode="equals"
                                                filterMatchModeOptions={numberFilterMatchModeOptions}
                                                body={this.verifiedBodyTemplate}
                                                filterElement={this.verifiedFilterTemplate}
                                                sortable
                                            ></Column>
                                        </ExtendedDataTable>
        );

        return table;
    }
}
